import React, { useState } from "react";
import { LineItemElementProps } from "../CreatorTools/LineItemDBCreationToolPage";

// Internal type for tracking selections in the permutation panel
interface PermutationSelection {
	elementIndex: number;
	selectedValue: string;
}

export interface LineItemPermutationPreviewPanelProps {
	elements: LineItemElementProps[];
	selectedPermutation: string | undefined;
	onPermutationChange: (permutation: string | undefined) => void;
}

const LineItemPermutationPreviewPanel: React.FC<LineItemPermutationPreviewPanelProps> = ({ 
	elements, 
	selectedPermutation, 
	onPermutationChange 
}) => {
	// Get default selections from elements (using defaultValue for each variable)
	const getDefaultSelections = (): PermutationSelection[] => {
		const selections: PermutationSelection[] = [];
		for (let i = 0; i < elements.length; i++) {
			const element = elements[i];
			if (element.type === "variable" && element.defaultValue) {
				selections.push({ elementIndex: i, selectedValue: element.defaultValue });
			}
		}
		return selections;
	};

	// Parse existing permutation string to internal selections (for editing existing items)
	const parsePermutationToSelections = (permStr: string | undefined): PermutationSelection[] => {
		// If no permutation string, use default values
		if (!permStr) return getDefaultSelections();
		
		// Try to match the permutation string against elements to figure out selections
		const selections: PermutationSelection[] = [];
		let remainingStr = permStr;
		
		for (let i = 0; i < elements.length; i++) {
			const element = elements[i];
			if (element.type === "text") {
				// Remove the text portion from remaining string
				remainingStr = remainingStr.replace(element.value, "").trim();
			} else if (element.type === "variable" && element.acceptableValues) {
				// Check if any acceptable value is in the remaining string
				for (const val of element.acceptableValues) {
					if (remainingStr.includes(val)) {
						selections.push({ elementIndex: i, selectedValue: val });
						remainingStr = remainingStr.replace(val, "").trim();
						break;
					}
				}
				// Check for empty value
				if (element.allowEmptyValue && !selections.find(s => s.elementIndex === i)) {
					selections.push({ elementIndex: i, selectedValue: "" });
				}
			}
		}
		
		return selections;
	};
	
	const [selections, setSelections] = useState<PermutationSelection[]>(() => 
		parsePermutationToSelections(selectedPermutation)
	);

	// Reset selections when elements or selectedPermutation changes (e.g., switching line items)
	React.useEffect(() => {
		const newSelections = parsePermutationToSelections(selectedPermutation);
		setSelections(newSelections);
		
		// If selectedPermutation is undefined but we have defaults, set the permutation to the default string
		if (selectedPermutation === undefined && newSelections.length > 0) {
			onPermutationChange(buildPermutationString(newSelections));
		}
	}, [elements, selectedPermutation]);

	// Build the preview text from current selections
	const buildPermutationString = (currentSelections: PermutationSelection[]): string => {
		return elements.map((element, index) => {
			if (element.type === "text") {
				return element.value;
			} else {
				const selection = currentSelections.find(s => s.elementIndex === index);
				return selection?.selectedValue ?? element.defaultValue ?? "";
			}
		}).join(" ").replace(/\s+/g, " ").trim();
	};

	const handleValueClick = (elementIndex: number, value: string) => {
		let newSelections = [...selections];
		const existingIndex = newSelections.findIndex(s => s.elementIndex === elementIndex);
		
		if (existingIndex >= 0) {
			// If clicking the same value, revert to default for this element
			if (newSelections[existingIndex].selectedValue === value) {
				const element = elements[elementIndex];
				if (element.type === "variable" && element.defaultValue) {
					// Revert to default value
					newSelections[existingIndex] = { elementIndex, selectedValue: element.defaultValue };
				} else {
					// No default, remove the selection
					newSelections = newSelections.filter(s => s.elementIndex !== elementIndex);
				}
			} else {
				// Otherwise, update the value
				newSelections[existingIndex] = { elementIndex, selectedValue: value };
			}
		} else {
			newSelections.push({ elementIndex, selectedValue: value });
		}
		
		setSelections(newSelections);
		
		// Always return the built string (even for defaults)
		onPermutationChange(buildPermutationString(newSelections));
	};

	const getSelectedValue = (elementIndex: number): string | undefined => {
		return selections.find(s => s.elementIndex === elementIndex)?.selectedValue;
	};
	
	return (
		<div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
			{/* Inline preview showing the line item being built */}
			<div className="mb-4 p-3 bg-white rounded border border-gray-200">
				<div className="text-xs text-gray-500 mb-2">Preview:</div>
				<div className="flex flex-wrap items-center gap-1">
					{elements.map((element, index) => {
						if (element.type === "text") {
							return (
								<span key={index} className="text-gray-800">{element.value}</span>
							);
						} else {
							const selectedValue = getSelectedValue(index);
							const displayValue = selectedValue ?? element.defaultValue ?? "____";
							const hasSelection = selectedValue !== undefined;
							return (
								<span 
									key={index} 
									className={`px-1.5 py-0.5 rounded ${
										hasSelection 
											? "bg-blue-100 text-blue-800 font-medium" 
											: "bg-gray-100 text-gray-500 italic"
									}`}
								>
									{displayValue || "(empty)"}
								</span>
							);
						}
					})}
				</div>
			</div>

			{/* Variable selection options */}
			<div className="text-xs text-gray-500 mb-2">Select values for each variable:</div>
			<div className="flex flex-wrap gap-4 items-start">
				{elements.map((element, index) => {
					if (element.type === "text") {
						return (
							<div key={index} className="flex items-center py-1">
								<span className="text-gray-400 text-sm">{element.value}</span>
							</div>
						);
					} else if (element.type === "variable" && element.acceptableValues) {
						const selectedValue = getSelectedValue(index);
						return (
							<div key={index} className="flex flex-col gap-1 p-2 bg-white rounded border border-gray-200">
								<div className="flex flex-wrap gap-1">
									{element.acceptableValues.map((value, valIndex) => {
										const isSelected = selectedValue === value;
										const isDefault = element.defaultValue === value;
										return (
											<button
												key={valIndex}
												type="button"
												onClick={() => handleValueClick(index, value)}
												className={`px-2 py-1 text-xs rounded border transition-all ${
													isSelected
														? "bg-blue-600 text-white border-blue-600"
														: isDefault
															? "bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100"
															: "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
												}`}
												title={isDefault ? "Default value" : undefined}
											>
												{value}
												{isDefault && !isSelected && <span className="ml-1 text-blue-500">*</span>}
											</button>
										);
									})}
								</div>
								{element.allowEmptyValue && (
									<button
										type="button"
										onClick={() => handleValueClick(index, "")}
										className={`px-2 py-1 text-xs rounded border transition-all ${
											selectedValue === ""
												? "bg-blue-600 text-white border-blue-600"
												: "bg-white text-gray-500 border-gray-300 hover:bg-gray-100"
										}`}
									>
										(empty)
									</button>
								)}
							</div>
						);
					}
					return null;
				})}
			</div>
			
			<div className="mt-3 text-xs text-gray-500">
				<span className="text-blue-500">*</span> = default value. Click to select a specific permutation.
			</div>
		</div>
	);
};

export default LineItemPermutationPreviewPanel;

